import { useEffect, useState } from 'react';

import CourseManager from '../components/CourseManager';
import ExerciseEditor from '../components/ExerciseEditor';
import MenuShell from '../components/MenuShell';
import ProgressSection from '../components/ProgressSection';
import { listExercisesByChapter } from '../features/instructor/instructorApi';
import { storeInstructorCourseId } from '../features/instructor/useInstructorCourseId';
import type { ExerciseAuthoring } from '../features/instructor/instructorApi';
import type { StudentProgress } from '../features/progress/progressApi';

interface ExerciseListProps {
  exercises: ExerciseAuthoring[];
  onSelect: (exercise: ExerciseAuthoring) => void;
}

function ExerciseList({ exercises, onSelect }: ExerciseListProps) {
  return (
    <ul>
      {exercises.map((exercise) => (
        <li key={exercise.id}>
          <button type="button" onClick={() => onSelect(exercise)}>
            {exercise.title} ({exercise.status})
          </button>
        </li>
      ))}
    </ul>
  );
}

function InstructorDashboard() {
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);
  const [selectedChapterId, setSelectedChapterId] = useState<string | null>(null);
  const [exercises, setExercises] = useState<ExerciseAuthoring[]>([]);
  const [editingExercise, setEditingExercise] = useState<ExerciseAuthoring | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const [selectedStudent, setSelectedStudent] = useState<StudentProgress | null>(null);

  useEffect(() => {
    let ignore = false;

    async function loadExercises() {
      if (!selectedChapterId) {
        if (!ignore) {
          setExercises([]);
        }
        return;
      }
      try {
        const result = await listExercisesByChapter(selectedChapterId);
        if (!ignore) {
          setExercises(result);
        }
      } catch (error) {
        if (!ignore) {
          setErrorMessage(
            error instanceof Error ? error.message : 'Impossible de charger les exercices.',
          );
        }
      }
    }

    void loadExercises();
    return () => {
      ignore = true;
    };
  }, [selectedChapterId, refreshToken]);

  function handleSelectCourse(courseId: string) {
    setSelectedCourseId(courseId);
    storeInstructorCourseId(courseId);
    setSelectedChapterId(null);
    setEditingExercise(null);
    setIsCreating(false);
    setSelectedStudent(null);
  }

  function handleSelectChapter(chapterId: string) {
    setSelectedChapterId(chapterId);
    setEditingExercise(null);
    setIsCreating(false);
  }

  function handleSelectExercise(exercise: ExerciseAuthoring) {
    setEditingExercise(exercise);
    setIsCreating(false);
  }

  function handleStartCreating() {
    setIsCreating(true);
    setEditingExercise(null);
  }

  function handleEditorCancel() {
    setIsCreating(false);
    setEditingExercise(null);
  }

  function handleExerciseSaved() {
    setEditingExercise(null);
    setIsCreating(false);
    setRefreshToken((current) => current + 1);
  }

  function handleExerciseDeleted() {
    setEditingExercise(null);
    setRefreshToken((current) => current + 1);
  }

  const isEditorOpen = isCreating || editingExercise !== null;

  return (
    <>
      <MenuShell courseId={selectedCourseId ?? ''} />
      <main
        style={{
          maxWidth: 960,
          margin: '3rem auto',
          padding: '0 1rem',
          display: 'grid',
          gap: '2rem',
        }}
      >
        <h1>Tableau de bord instructeur</h1>
        {errorMessage && <p role="alert">{errorMessage}</p>}

        <CourseManager
          selectedCourseId={selectedCourseId}
          selectedChapterId={selectedChapterId}
          onSelectCourse={handleSelectCourse}
          onSelectChapter={handleSelectChapter}
          refreshToken={refreshToken}
        />

        {selectedChapterId && (
          <section>
            <h2>Exercices du chapitre</h2>
            <ExerciseList exercises={exercises} onSelect={handleSelectExercise} />
            {!isEditorOpen && (
              <button type="button" onClick={handleStartCreating}>
                Nouvel exercice
              </button>
            )}

            {isEditorOpen && (
              <ExerciseEditor
                key={editingExercise?.id ?? 'new'}
                chapterId={selectedChapterId}
                exercise={editingExercise}
                onSaved={handleExerciseSaved}
                onCancel={handleEditorCancel}
                onDeleted={handleExerciseDeleted}
              />
            )}
          </section>
        )}

        {selectedCourseId && (
          <>
            <p>
              <a href={`/courses/${selectedCourseId}/leaderboard`}>Classement en temps réel</a>
            </p>
            <ProgressSection
              key={selectedCourseId}
              courseId={selectedCourseId}
              selectedStudent={selectedStudent}
              onSelectStudent={setSelectedStudent}
            />
          </>
        )}
      </main>
    </>
  );
}

export default InstructorDashboard;
